import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { getStoredAccountType } from "../auth";
import { useAdminDiagnoses } from "../hooks/useAdminDiagnoses";
import { cn } from "@/shared/lib/cn";
import type { StandardDiagnosticCode } from "../types";

type SortKey = "code" | "label" | "type" | "description";

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function StandardDiagnosticCodesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.standardDiagnostics;
  const { pathname } = useLocation();
  const { diagnostics, loading, error, usingApi, reload } = useAdminDiagnoses();
  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("code");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    void reload();
  }, [pathname, reload]);

  const filtered = useMemo(() => {
    return diagnostics.filter((item) => {
      const haystack =
        `${item.code} ${item.label} ${item.type} ${item.description}`.toLowerCase();
      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      return byTopSearch && byTableSearch;
    });
  }, [diagnostics, topSearch, tableSearch]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const cmp = String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setPage(1);
    void reload();
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const handleExportCsv = () => {
    const headers = [
      labels.columns.code,
      labels.columns.name,
      labels.columns.type,
      labels.columns.description,
    ];
    const rows = [
      headers.join(","),
      ...sorted.map((item: StandardDiagnosticCode) =>
        [item.code, item.label, item.type, item.description]
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "diagnostics-standards.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: { key: SortKey; label: string }[] = [
    { key: "code", label: labels.columns.code },
    { key: "label", label: labels.columns.name },
    { key: "type", label: labels.columns.type },
    { key: "description", label: labels.columns.description },
  ];

  const renderCell = (value: string) => value || "—";

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={[
          { label: t.safecheck.nav.systemSection },
          { label: labels.title },
        ]}
      />

      {(usingApi || error) && (
        <p className={`mb-3 text-xs ${error ? "text-pink" : "text-muted"}`}>
          {error ?? (loading ? "Chargement…" : `${diagnostics.length} diagnostic(s) API`)}
        </p>
      )}

      <div className="theme-card">
        <TableToolbar
          search={topSearch}
          onSearchChange={(value) => {
            setTopSearch(value);
            setPage(1);
          }}
          searchPlaceholder={labels.searchPlaceholder}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
          onExport={handleExportCsv}
          exportLabel={labels.exportCsv}
          onReset={resetFilters}
          resetLabel={t.safecheck.reset}
        />

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-[11px] font-semibold tracking-wide text-muted"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="inline-flex items-center gap-1 uppercase"
                    >
                      {column.label}
                      <span className="flex flex-col">
                        <ChevronUp
                          size={10}
                          className={cn(
                            "-mb-1",
                            sortKey === column.key && sortDir === "asc" && "text-primary",
                          )}
                        />
                        <ChevronDown
                          size={10}
                          className={cn(
                            sortKey === column.key && sortDir === "desc" && "text-primary",
                          )}
                        />
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((item, index) => (
                <tr
                  key={item.id}
                  className={cn(
                    "border-b border-border last:border-0 hover:bg-card-muted/30",
                    index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                  )}
                >
                  <td className="px-4 py-3 text-sm font-semibold text-pink">{item.code}</td>
                  <td className="px-4 py-3 text-sm font-bold uppercase text-foreground">
                    {item.label}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-teal px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                      {item.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted">{renderCell(item.description)}</td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing} {sorted.length === 0 ? 0 : start + 1}{" "}
            {labels.pagination.to} {Math.min(start + pageSize, sorted.length)}{" "}
            {labels.pagination.of} {sorted.length} {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={currentPage === 1}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                  pageNumber === currentPage
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:bg-card-muted",
                )}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={currentPage === totalPages}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
